<template>
  <AisInstantSearch
    :class="wrapperClasses"
    :future="{
      preserveSharedStateOnUnmount: false,
    }"
    :index-name="indexName"
    :routing="routing"
    :search-client="searchClient"
  >
    <AisConfigure
      :filters="filters"
      :hits-per-page.camel="hitsPerPage"
    />

    <slot />
  </AisInstantSearch>
</template>

<script lang="ts" setup>
import { onMounted } from 'vue'
import Client from '@searchkit/instantsearch-client'
import { AisConfigure, AisInstantSearch } from 'vue-instantsearch/vue3/es'

import { env } from '@js/utils/env'
import { getSearchRouting } from '@js/utils/searchRouting'

defineOptions({
  name: 'BaseSearchWrapper',
})

/** Raw Elasticsearch aggregation payload, passed through untouched by the proxy. */
type RawAggregations = Record<string, unknown>

type InstantSearchResult = {
  _rawAggregations?: RawAggregations
}

type SearchResponse = {
  results?: InstantSearchResult[]
}

type SearchClient = {
  // The real client is typed Promise<any> and can resolve to an error body or [].
  search: (requests: unknown[]) => Promise<unknown>
  searchForFacetValues: (...args: unknown[]) => Promise<unknown>
  clearCache: () => Promise<void>
}

const isSearchResponse = (value: unknown): value is Required<SearchResponse> =>
  Array.isArray((value as SearchResponse)?.results)

/** One well-formed empty result per request, so InstantSearch renders "no results". */
const emptyResults = (requests: unknown[]) => ({
  results: requests.map(() => ({
    hits: [],
    nbHits: 0,
    page: 0,
    nbPages: 0,
    hitsPerPage: 0,
    processingTimeMS: 0,
    exhaustiveNbHits: true,
    query: '',
    params: '',
    facets: {},
  })),
})

const props = withDefaults(
  defineProps<{
    filters?: string
    indexName: string
    stateKey?: string
    hitsPerPage?: number
    wrapperClasses?: string
    /**
     * Which server-side search profile to use. Profiles own the index mappings, facets,
     * sorting and query logic, and live in the proxy — see
     * azure-functions/elasticsearch-proxy/src/lib/profiles/.
     */
    searchProfile?: string
  }>(),
  {
    filters: '',
    stateKey: '',
    hitsPerPage: 10,
    wrapperClasses: 'page-grid py-6',
    searchProfile: 'wcei',
  },
)

const emit = defineEmits<{
  searchClientLoaded: [searchClient: SearchClient]
  /**
   * Raw Elasticsearch aggregations a profile passed through for the current response.
   * Empty when the profile declares no `passthroughAggregations`.
   */
  aggregations: [aggregations: RawAggregations]
}>()

// Search settings (index config, facets, query builder) live server-side in the Azure
// Function proxy — this client only knows where to POST and which profile to ask for.
const baseSearchClient = Client({
  url:
    env.elasticsearch.PROXY_URL ||
    (() => {
      throw new Error('ELASTICSEARCH_PROXY_URL is not configured')
    })(),
  headers: { 'X-Search-Profile': props.searchProfile },
}) as unknown as SearchClient

// Delegate explicitly rather than spreading `baseSearchClient`. It is a class instance, so
// `search`, `clearCache` and `searchForFacetValues` live on the prototype and a spread
// would silently drop all three.
const searchClient: SearchClient = {
  search: async (requests) => {
    const response = await baseSearchClient.search(requests)

    // The client resolves — and caches — whatever JSON came back, including the proxy's
    // error bodies, which would otherwise wedge this request key until a reload.
    if (!isSearchResponse(response)) {
      await baseSearchClient.clearCache()
      emit('aggregations', {})
      return emptyResults(requests)
    }

    // Runs on cache hits too, since the cache sits inside baseSearchClient.search().
    emit('aggregations', response.results[0]?._rawAggregations ?? {})

    return response
  },
  searchForFacetValues: (...args) => baseSearchClient.searchForFacetValues(...args),
  clearCache: () => baseSearchClient.clearCache(),
}

const routing = getSearchRouting(props.indexName, props.stateKey)

onMounted(() => {
  emit('searchClientLoaded', searchClient)
})
</script>
